import React, { useState, createElement } from 'react';
import { Popup } from './common/Popup';
export function Footer() {
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const openPopup = (popupId: string) => {
    // Special handling for privacy policy and terms - download as text files
    if (popupId === 'privacy') {
      downloadPrivacyPolicy();
      return;
    }
    if (popupId === 'terms') {
      downloadTerms();
      return;
    }
    setActivePopup(popupId);
  };
  const closePopup = () => {
    setActivePopup(null);
  };
  const downloadPrivacyPolicy = () => {
    const privacyContent = `# Kraya Privacy Policy
_Last Updated: July 15, 2025_
At Kraya, your privacy is important to us. This policy outlines how we collect, use, and safeguard your data.
## What We Collect
- Card-linked transaction data (for rewards purposes only)
- Basic contact information (email, name)
- Device and location data (to surface nearby offers)
## How We Use Your Data
- To automatically issue rewards when you shop at local stores
- To show you relevant offers and recommendations
- To improve app performance and detect fraud
## What We Don't Do
- We never sell your data.
- We never share personal details without your explicit permission.
## Data Retention
Your data is stored securely and retained only as long as needed for rewards and compliance.
## Contact Us
For any questions, email us at privacy@kraya.app`;
    // Create a Blob from the text content
    const blob = new Blob([privacyContent], {
      type: 'text/plain'
    });
    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);
    // Create a temporary anchor element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kraya_privacy_policy.txt';
    // Append to the document, click, and remove
    document.body.appendChild(link);
    link.click();
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const downloadTerms = () => {
    const termsContent = `# Kraya Terms of Use
_Last Updated: July 15, 2025_
Welcome to Kraya. By using our platform, you agree to the following terms.
## Eligibility
You must be 18+ and legally capable of forming a binding contract.
## Use of Service
You agree not to misuse our platform. You will only use it as intended — to discover local stores and earn rewards.
## Reward Program
Rewards may vary by store. We reserve the right to modify or cancel offers at any time.
## Account Responsibility
You're responsible for maintaining the confidentiality of your login credentials.
## Limitation of Liability
Kraya is not liable for issues arising from third-party merchants or payment processors.
## Contact Us
Questions? Reach out at support@kraya.app`;
    // Create a Blob from the text content
    const blob = new Blob([termsContent], {
      type: 'text/plain'
    });
    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);
    // Create a temporary anchor element to trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.download = 'kraya_terms_of_use.txt';
    // Append to the document, click, and remove
    document.body.appendChild(link);
    link.click();
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return <>
      {/* Layered curved transition - three layers */}
      <div className="relative h-24 overflow-hidden bg-[#f8f5ff]" data-id="element-120">
        {/* First curve - darkest shade */}
        <div className="absolute w-full h-32 -bottom-14 bg-[#8860D0] rounded-[50%_50%_0_0] transform scale-x-150" data-id="element-121"></div>
        {/* Second curve - medium shade */}
        <div className="absolute w-full h-32 -bottom-12 bg-[#9F7AE5] rounded-[50%_50%_0_0] transform scale-x-125 z-10" data-id="element-122"></div>
        {/* Third curve - regular footer color */}
        <div className="absolute w-full h-32 -bottom-10 bg-[#B490FF] rounded-[50%_50%_0_0] transform scale-x-110 z-20" data-id="element-123"></div>
      </div>
      <footer className="bg-[#B490FF] relative z-30 -mt-2 pt-2" data-id="element-124">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-125">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-126">
            <div className="md:col-span-2" data-id="element-127">
              <div className="mb-3" data-id="element-128">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pr8aEPnyhYBiZB5ruVSA7e/image.png" alt="Kraya Logo" className="h-40" data-id="element-129" />
              </div>
              <p className="text-white max-w-xs" data-id="element-130">
                Blending online convenience with real-world shopping
                experiences. Automatic rewards for local shopping.
              </p>
              <div className="mt-3 flex space-x-4" data-id="element-131">
                {['twitter', 'instagram', 'facebook'].map(social => <a key={social} href="#" className="text-white/70 hover:text-white" data-id="element-132">
                    <span className="sr-only" data-id="element-133">{social}</span>
                    <div className="h-6 w-6 bg-white/20 rounded-full" data-id="element-134"></div>
                  </a>)}
              </div>
            </div>
            <div data-id="element-135">
              <h3 className="font-semibold text-white mb-3" data-id="element-136">🏢 Company</h3>
              <ul className="space-y-2" data-id="element-137">
                {[{
                id: 'about',
                label: 'About'
              }, {
                id: 'team',
                label: 'Team'
              }, {
                id: 'careers',
                label: 'Careers'
              }, {
                id: 'press',
                label: 'Press'
              }].map(item => <li key={item.id} data-id="element-138">
                    <button onClick={() => openPopup(item.id)} className="text-white/80 hover:text-white focus:outline-none" data-id="element-139">
                      {item.label}
                    </button>
                  </li>)}
              </ul>
            </div>
            <div data-id="element-140">
              <h3 className="font-semibold text-white mb-3" data-id="element-141">📚 Resources</h3>
              <ul className="space-y-2" data-id="element-142">
                {[{
                id: 'for-merchants',
                label: 'For Merchants'
              }, {
                id: 'help-center',
                label: 'Help Center'
              }, {
                id: 'privacy',
                label: 'Privacy'
              }, {
                id: 'terms',
                label: 'Terms'
              }].map(item => <li key={item.id} data-id="element-143">
                    <button onClick={() => openPopup(item.id)} className="text-white/80 hover:text-white focus:outline-none" data-id="element-144">
                      {item.label}
                    </button>
                  </li>)}
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-purple-300/30" data-id="element-145">
            <p className="text-white/70 text-sm text-center" data-id="element-146">
              © {new Date().getFullYear()} Kraya. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      {/* Company Popups */}
      <Popup isOpen={activePopup === 'about'} onClose={closePopup} title="About" data-id="element-147">
        <p className="text-gray-700" data-id="element-148">
          Kraya is on a mission to make local shopping rewarding again —
          blending online convenience with the magic of discovering and
          supporting neighborhood spots.
        </p>
      </Popup>
      <Popup isOpen={activePopup === 'team'} onClose={closePopup} title="Team" data-id="element-149">
        <p className="text-gray-700" data-id="element-150">
          We're a small, scrappy team of product thinkers, engineers, and
          creators passionate about real-world commerce. Want to build with us?
        </p>
      </Popup>
      <Popup isOpen={activePopup === 'careers'} onClose={closePopup} title="Careers" data-id="element-151">
        <p className="text-gray-700" data-id="element-152">
          Help shape the future of hybrid shopping. Check back soon — we're not
          hiring yet, but we will be soon.
        </p>
      </Popup>
      <Popup isOpen={activePopup === 'press'} onClose={closePopup} title="Press" data-id="element-153">
        <p className="text-gray-700" data-id="element-154">
          For media inquiries, product assets, and partnership stories, reach
          out to press@kraya.app
        </p>
      </Popup>
      {/* Resources Popups */}
      <Popup isOpen={activePopup === 'for-merchants'} onClose={closePopup} title="For Merchants" data-id="element-155">
        <div className="space-y-4" data-id="element-156">
          <p className="text-gray-700" data-id="element-157">
            List your store on Kraya and turn everyday visits into lasting
            loyalty. No hardware. No headaches. Just better repeat business.
          </p>
          <div data-id="element-158">
            <a href="#merchants" onClick={closePopup} className="inline-flex items-center text-[#8860D0] font-medium hover:underline" data-id="element-159">
              👉 Learn More
            </a>
          </div>
        </div>
      </Popup>
      <Popup isOpen={activePopup === 'help-center'} onClose={closePopup} title="Help Center" data-id="element-160">
        <p className="text-gray-700" data-id="element-161">
          Need support? Visit our Help Center to explore FAQs, tutorials, and
          account assistance. (Coming soon.)
        </p>
      </Popup>
      {/* Note: Privacy and Terms popups not needed anymore as they now download files */}
    </>;
}